import { Routes, Route, Link } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import Layout from '../components/layout/Layout';
import { FileSearch } from 'lucide-react';

// Loading Component
const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
);

// Lazy loaded pages
// Main pages
const Home = lazy(() => import('../pages/Home'));
const AboutOverview = lazy(() => import('../pages/AboutOverview'));
const DataSecurity = lazy(() => import('../pages/products/DataSecurity'));
const AccessManagement = lazy(() => import('../pages/products/AccessManagement'));
const Virtualization = lazy(() => import('../pages/products/Virtualization'));
const Infrastructure = lazy(() => import('../pages/products/Infrastructure'));
const Backup = lazy(() => import('../pages/products/Backup'));

// Lazy loaded sub-pages
const AboutCustomers = lazy(() => import('../pages/AboutCustomers'));
const AboutHistory = lazy(() => import('../pages/AboutHistory'));
const AboutGreetings = lazy(() => import('../pages/AboutGreetings'));
const AboutOrgChart = lazy(() => import('../pages/AboutOrgChart'));
const AboutLocation = lazy(() => import('../pages/AboutLocation'));
const AboutCareer = lazy(() => import('../pages/AboutCareer'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const SolutionRoutes = lazy(() => import('./SolutionRoutes'));
const LunaNetworkHSM = lazy(() => import('../pages/products/LunaNetworkHSM'));
const LunaPCIeHSM = lazy(() => import('../pages/products/LunaPCIeHSM'));
const LunaBackupHSM = lazy(() => import('../pages/products/LunaBackupHSM'));
const LunaUSBHSM = lazy(() => import('../pages/products/LunaUSBHSM'));
const ProtectServerHSM = lazy(() => import('../pages/products/ProtectServerHSM'));
const CryptoCommandCenter = lazy(() => import('../pages/products/CryptoCommandCenter'));
const DataSecurityPlatform = lazy(() => import('../pages/products/DataSecurityPlatform'));
const CipherTrustManager = lazy(() => import('../pages/products/CipherTrustManager'));
const CipherTrustCloudKeyManager = lazy(() => import('../pages/products/CipherTrustCloudKeyManager'));
const DoubleKeyEncryption = lazy(() => import('../pages/products/DoubleKeyEncryption'));
const SafeNetTrustedAccess = lazy(() => import('../pages/products/SafeNetTrustedAccess'));
const CloudSSO = lazy(() => import('../pages/products/CloudSSO'));
const CloudSSOIntegrations = lazy(() => import('../pages/products/CloudSSOIntegrations'));
const SmartCards = lazy(() => import('../pages/products/SmartCards'));
const MultiFactorAuthentication = lazy(() => import('../pages/products/MultiFactorAuthentication'));
const Authenticators = lazy(() => import('../pages/products/Authenticators'));
const OnPremAuthenticationManagement = lazy(() => import('../pages/products/OnPremAuthenticationManagement'));
const WorkforceIdentity = lazy(() => import('../pages/products/WorkforceIdentity'));
const CustomerIdentity = lazy(() => import('../pages/products/CustomerIdentity'));
const B2BIdentity = lazy(() => import('../pages/products/B2BIdentity'));
const GigWorkerIdentity = lazy(() => import('../pages/products/GigWorkerIdentity'));
const SAMLAuthentication = lazy(() => import('../pages/products/SAMLAuthentication'));
const PKIUSBAuthentication = lazy(() => import('../pages/products/PKIUSBAuthentication'));
const FIDODevices = lazy(() => import('../pages/products/FIDODevices'));
const OTPTokens = lazy(() => import('../pages/products/OTPTokens'));
const SecurityApplications = lazy(() => import('../pages/products/SecurityApplications'));
const PasswordlessAuthentication = lazy(() => import('../pages/products/PasswordlessAuthentication'));
const SmartCardReaders = lazy(() => import('../pages/products/SmartCardReaders'));
const TransparentEncryption = lazy(() => import('../pages/products/TransparentEncryption'));
const DataSecurityPostureManagement = lazy(() => import('../pages/products/DataSecurityPostureManagement'));
const DevSecOpsSecurity = lazy(() => import('../pages/products/DevSecOpsSecurity'));
const AuthenticationAsAService = lazy(() => import('../pages/products/AuthenticationAsAService'));
const IdentityAsAService = lazy(() => import('../pages/products/IdentityAsAService'));
const OneWelcomePlatform = lazy(() => import('../pages/products/OneWelcomePlatform'));
const MobilePassOTP = lazy(() => import('../pages/products/MobilePassOTP'));
const KubernetesEncryption = lazy(() => import('../pages/products/KubernetesEncryption'));
const UserSpaceEncryption = lazy(() => import('../pages/products/UserSpaceEncryption'));
const ApplicationDataProtection = lazy(() => import('../pages/products/ApplicationDataProtection'));
const Tokenization = lazy(() => import('../pages/products/Tokenization'));
const ApplicationSecurity = lazy(() => import('../pages/products/ApplicationSecurity'));
const AIApplicationSecurity = lazy(() => import('../pages/products/application-security/AIApplicationSecurity'));
const WebApplicationFirewall = lazy(() => import('../pages/products/application-security/WebApplicationFirewall'));
const AdvancedBotProtection = lazy(() => import('../pages/products/application-security/AdvancedBotProtection'));
const APISecurity = lazy(() => import('../pages/products/application-security/APISecurity'));
const DDoSProtection = lazy(() => import('../pages/products/application-security/DDoSProtection'));
const ClientSideProtection = lazy(() => import('../pages/products/application-security/ClientSideProtection'));
const AccountTakeoverProtection = lazy(() => import('../pages/products/application-security/AccountTakeoverProtection'));
const ContentDeliveryNetwork = lazy(() => import('../pages/products/application-security/ContentDeliveryNetwork'));
const DataProtectionGateway = lazy(() => import('../pages/products/DataProtectionGateway'));
const DatabaseSecurity = lazy(() => import('../pages/products/DatabaseSecurity'));
const StaticDataMasking = lazy(() => import('../pages/products/StaticDataMasking'));
const IntelligentDataProtection = lazy(() => import('../pages/products/IntelligentDataProtection'));
const DataDiscoveryClassification = lazy(() => import('../pages/products/DataDiscoveryClassification'));
const SecretsManagement = lazy(() => import('../pages/products/SecretsManagement'));
const HighSpeedEncryption = lazy(() => import('../pages/products/HighSpeedEncryption'));
const HardwareSecurityModules = lazy(() => import('../pages/products/HardwareSecurityModules'));
const DataEncryptionSolutions = lazy(() => import('../pages/products/DataEncryptionSolutions'));
const RightEncryptionApproach = lazy(() => import('../pages/products/RightEncryptionApproach'));
const FIPSLevel3Compliance = lazy(() => import('../pages/products/FIPSLevel3Compliance'));
const VeeamDataProtection = lazy(() => import('../pages/products/VeeamDataProtection'));
const ManagedBackup = lazy(() => import('../pages/products/ManagedBackup'));
const StorSafe = lazy(() => import('../pages/products/StorSafe'));
const StorGuard = lazy(() => import('../pages/products/StorGuard'));
const StorSight = lazy(() => import('../pages/products/StorSight'));
const StorageVirtualization = lazy(() => import('../pages/products/StorageVirtualization'));
const VMware = lazy(() => import('../pages/products/VMware'));
const Proxmox = lazy(() => import('../pages/products/Proxmox'));
const DataProtectionOnDemand = lazy(() => import('../pages/products/DataProtectionOnDemand'));

// Placeholder for other pages - to be implemented
const PlaceholderPage = ({ title }: { title: string }) => (
    <div className="pt-32 pb-20 container mx-auto px-4 min-h-screen">
        <h1 className="text-4xl font-bold mb-8 text-center">{title}</h1>
        <div className="bg-white border border-gray-100 rounded-3xl p-12 md:p-20 text-center shadow-sm max-w-2xl mx-auto">
            <div className="flex justify-center mb-8">
                <div className="p-6 bg-blue-50 rounded-full">
                    <FileSearch className="w-16 h-16 text-blue-600" />
                </div>
            </div>
            <p className="text-gray-600 text-lg mb-10">
                요청하신 페이지를 찾을 수 없습니다.<br />
                입력하신 주소가 정확한지 다시 한번 확인해 주시기 바랍니다.
            </p>
            <Link
                to="/"
                className="inline-flex items-center px-8 py-3 bg-blue-600 text-white font-medium rounded-full hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
            >
                메인으로 돌아가기
            </Link>
        </div>
    </div>
);

const AppRoutes = () => {
    return (
        <Layout>
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/" element={<Home />} />

                    {/* Products */}
                    <Route path="/products/data-security" element={<DataSecurity />} />
                    <Route path="/products/data-security/luna-network-hsm" element={<LunaNetworkHSM />} />
                    <Route path="/products/data-security/luna-pcie-hsm" element={<LunaPCIeHSM />} />
                    <Route path="/products/data-security/luna-usb-hsm" element={<LunaUSBHSM />} />
                    <Route path="/products/data-security/luna-backup-hsm" element={<LunaBackupHSM />} />
                    <Route path="/products/data-security/protectserver-hsm" element={<ProtectServerHSM />} />
                    <Route path="/products/data-security/crypto-command-center" element={<CryptoCommandCenter />} />
                    <Route path="/products/data-security/ciphertrust-platform" element={<DataSecurityPlatform />} />
                    <Route path="/products/data-security/transparent-encryption" element={<TransparentEncryption />} />
                    <Route path="/products/data-security/ciphertrust-manager" element={<CipherTrustManager />} />
                    <Route path="/products/data-security/ciphertrust-cloud-key-manager" element={<CipherTrustCloudKeyManager />} />
                    <Route path="/products/data-security/double-key-encryption" element={<DoubleKeyEncryption />} />
                    <Route path="/products/data-security/dspm" element={<DataSecurityPostureManagement />} />
                    <Route path="/products/data-security/devsecops" element={<DevSecOpsSecurity />} />
                    <Route path="/products/data-security/kubernetes-encryption" element={<KubernetesEncryption />} />
                    <Route path="/products/data-security/userspace-encryption" element={<UserSpaceEncryption />} />
                    <Route path="/products/data-security/application-data-protection" element={<ApplicationDataProtection />} />
                    <Route path="/products/data-security/tokenization" element={<Tokenization />} />
                    <Route path="/products/data-security/data-protection-gateway" element={<DataProtectionGateway />} />
                    <Route path="/products/data-security/database-security" element={<DatabaseSecurity />} />
                    <Route path="/products/data-security/static-data-masking" element={<StaticDataMasking />} />
                    <Route path="/products/data-security/intelligent-data-protection" element={<IntelligentDataProtection />} />
                    <Route path="/products/data-security/ddc" element={<DataDiscoveryClassification />} />
                    <Route path="/products/data-security/secrets-management" element={<SecretsManagement />} />
                    <Route path="/products/data-security/high-speed-encryption" element={<HighSpeedEncryption />} />
                    <Route path="/products/data-security/hsm" element={<HardwareSecurityModules />} />
                    <Route path="/products/data-security/data-encryption" element={<DataEncryptionSolutions />} />
                    <Route path="/products/data-security/dpod" element={<DataProtectionOnDemand />} />
                    <Route path="/products/application-security" element={<ApplicationSecurity />} />
                    <Route path="/products/application-security/ai-application-security" element={<AIApplicationSecurity />} />
                    <Route path="/products/application-security/web-application-firewall-waf" element={<WebApplicationFirewall />} />
                    <Route path="/products/application-security/advanced-bot-protection-management" element={<AdvancedBotProtection />} />
                    <Route path="/products/application-security/api-security" element={<APISecurity />} />
                    <Route path="/products/application-security/ddos-protection-services" element={<DDoSProtection />} />
                    <Route path="/products/application-security/client-side-protection-csp" element={<ClientSideProtection />} />
                    <Route path="/products/application-security/account-takeover-protection" element={<AccountTakeoverProtection />} />
                    <Route path="/products/application-security/content-delivery-network-cdn" element={<ContentDeliveryNetwork />} />

                    {/* Access Management */}
                    <Route path="/products/access-management" element={<AccessManagement />} />
                    <Route path="/products/access-management/safenet-trusted-access" element={<SafeNetTrustedAccess />} />
                    <Route path="/products/access-management/saml-authentication" element={<SAMLAuthentication />} />
                    <Route path="/products/access-management/smart-cards" element={<SmartCards />} />
                    <Route path="/products/access-management/cloud-sso" element={<CloudSSO />} />
                    <Route path="/products/access-management/cloud-sso/integrations" element={<CloudSSOIntegrations />} />
                    <Route path="/products/access-management/multi-factor-authentication" element={<MultiFactorAuthentication />} />
                    <Route path="/products/access-management/authenticators" element={<Authenticators />} />
                    <Route path="/products/access-management/pki-usb-authentication" element={<PKIUSBAuthentication />} />
                    <Route path="/products/access-management/fido-devices" element={<FIDODevices />} />
                    <Route path="/products/access-management/otp-tokens" element={<OTPTokens />} />
                    <Route path="/products/access-management/authentication-as-a-service" element={<AuthenticationAsAService />} />
                    <Route path="/products/access-management/identity-as-a-service" element={<IdentityAsAService />} />
                    <Route path="/products/access-management/onewelcome-platform" element={<OneWelcomePlatform />} />
                    <Route path="/products/access-management/mobilepass-otp" element={<MobilePassOTP />} />
                    <Route path="/products/access-management/on-prem-authentication-management" element={<OnPremAuthenticationManagement />} />
                    <Route path="/products/access-management/security-applications" element={<SecurityApplications />} />
                    <Route path="/products/access-management/passwordless-authentication" element={<PasswordlessAuthentication />} />
                    <Route path="/products/access-management/smart-card-readers" element={<SmartCardReaders />} />
                    <Route path="/products/access-management/workforce-identity" element={<WorkforceIdentity />} />
                    <Route path="/products/access-management/customer-identity" element={<CustomerIdentity />} />
                    <Route path="/products/access-management/b2b-identity" element={<B2BIdentity />} />
                    <Route path="/products/access-management/gig-worker-identity" element={<GigWorkerIdentity />} />
                    <Route path="/products/data-security/right-encryption-approach" element={<RightEncryptionApproach />} />
                    <Route path="/products/data-security/fips-level3-compliance" element={<FIPSLevel3Compliance />} />
                    <Route path="/products/backup/veeam" element={<VeeamDataProtection />} />
                    <Route path="/products/backup/managed" element={<ManagedBackup />} />

                    <Route path="/products/virtualization" element={<Virtualization />} />
                    <Route path="/products/storage-virtualization" element={<StorageVirtualization />} />
                    <Route path="/products/virtualization/vmware" element={<VMware />} />
                    <Route path="/products/virtualization/proxmox" element={<Proxmox />} />
                    <Route path="/products/virtualization/storsafe" element={<StorSafe />} />
                    <Route path="/products/virtualization/storguard" element={<StorGuard />} />
                    <Route path="/products/virtualization/storsight" element={<StorSight />} />
                    <Route path="/products/infrastructure" element={<Infrastructure />} />
                    <Route path="/products/backup" element={<Backup />} />

                    {/* Solutions */}
                    <Route path="/solutions/*" element={<SolutionRoutes />} />

                    {/* About */}
                    <Route path="/about/overview" element={<AboutOverview />} />
                    <Route path="/about/greetings" element={<AboutGreetings />} />
                    <Route path="/about/org-chart" element={<AboutOrgChart />} />
                    <Route path="/about/history" element={<AboutHistory />} />
                    <Route path="/about/customers" element={<AboutCustomers />} />
                    <Route path="/about/location" element={<AboutLocation />} />
                    <Route path="/about/career" element={<AboutCareer />} />

                    {/* Redirect /contact to /about/location or just use it */}
                    <Route path="/contact" element={<AboutLocation />} />

                    {/* Policy */}
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                    {/* 404 */}
                    <Route path="*" element={<PlaceholderPage title="404 - 페이지를 찾을 수 없습니다" />} />
                </Routes>
            </Suspense>
        </Layout>
    );
};

export default AppRoutes;
